import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { hasVisibleStackedChartValue } from '@/features/executionRuns/pages/executionRunsCharts';
import { getErrorCopy } from '@/shared/copy/errors';
import { s, typographyTokens } from '@/theme/tokens';
import { StatusBanner } from '@/ui';
import { ChartEmptyState, CHART_EMPTY_STATE_FILTER_HINT } from '@/ui/composites';
import { Box, PanelHeadingText } from '@/ui/primitives';
import { formatChartTooltipDateLabel } from '@/ui/primitives/chartDateLabel';
import { StackedBarChart } from '@/ui/primitives/StackedBarChart';
import { StackedBarChartSkeleton } from '@/ui/primitives/StackedBarChartSkeleton';
import { chartLoadErrorBannerSx } from './chartLoadErrorBannerSx';
import { type StackedMetricsStatusChartPanelLayout } from './StackedMetricsStatusChartSection.layout';
import {
  formatStackedStatusChartLabel,
  renderStatusChartLegendLoadingState,
} from './StackedMetricsStatusChartSection.states';
import { StatusChartPanel } from './StatusChartPanel';
import type { StatusPieDatum } from './StackedMetricsStatusChartSection.panelTypes';
import type {
  MetricsStatusChartPanelDisplayModel,
  MetricsStatusChartPanelModelToolbarState,
} from './useMetricsStatusChartPanelModel';
import type { MetricsToolbarTagOption } from '@/features/shared/components/metricsToolbar/metricsToolbarTagOptions';
import type { TableStatusOption } from '@/features/shared/components/tableSystem/tableToolbar.types';

const STATUS_TIMELINE_SKELETON_PLOT_AREA_HEIGHT_PX = 257;

type StatusStackedChartPanelProps<TStatus extends string> = {
  title: string;
  searchAriaLabel: string;
  statusOptions: Array<TableStatusOption<TStatus>>;
  statusFilterLabel: string;
  availableTags: MetricsToolbarTagOption[];
  loading: boolean;
  showPlaceholder?: boolean;
  toolbarState: MetricsStatusChartPanelModelToolbarState<TStatus>;
  statusPanel: StackedMetricsStatusChartPanelLayout;
  statusChartVisualHeight: number;
  legendData: StatusPieDatum[];
  legendLoading: boolean;
  statusEmptyTitle: string;
  statusChartData: MetricsStatusChartPanelDisplayModel['statusChartData'];
  showStatusChartSkeleton: boolean;
  showChartErrorState: boolean;
  suppressAnimation: boolean;
  showAllXAxisLabels?: boolean;
};

export const StatusStackedChartPanel = function StatusStackedChartPanel<TStatus extends string>({
  title,
  searchAriaLabel,
  statusOptions,
  statusFilterLabel,
  availableTags,
  loading,
  showPlaceholder = false,
  toolbarState,
  statusPanel,
  statusChartVisualHeight,
  legendData,
  legendLoading,
  statusEmptyTitle,
  statusChartData,
  showStatusChartSkeleton,
  showChartErrorState,
  suppressAnimation,
  showAllXAxisLabels = true,
}: StatusStackedChartPanelProps<TStatus>): ReactElement {
  const chartHostRef = useRef<HTMLDivElement | null>(null);
  const [chartHeightPx, setChartHeightPx] = useState<number>(statusChartVisualHeight);

  const legendNode = useMemo((): ReactElement | null => {
    if (legendLoading) return renderStatusChartLegendLoadingState();
    if (legendData.length === 0) return null;
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: s('lg'),
          rowGap: s('xs'),
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        {legendData.map((entry, index) => (
          <Box
            key={`${entry.id}-${String(index)}`}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: s('md'),
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: s('md'),
                height: s('md'),
                borderRadius: s('xxs'),
                backgroundColor: entry.color ?? 'text.secondary',
                flexShrink: 0,
              }}
            />
            <PanelHeadingText
              component="span"
              sx={{
                fontSize: typographyTokens.caption.fontSize,
                fontWeight: typographyTokens.caption.fontWeight,
                lineHeight: (muiTheme) => muiTheme.typography.label.lineHeight,
                whiteSpace: 'nowrap',
              }}
            >
              {entry.name}
            </PanelHeadingText>
          </Box>
        ))}
      </Box>
    );
  }, [legendData, legendLoading]);

  useEffect(() => {
    const element = chartHostRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    let rafId = 0;
    const measure = (): void => {
      const bounds = element.getBoundingClientRect();
      const nextHeight = Math.floor(bounds.height);
      if (!Number.isFinite(nextHeight) || nextHeight <= 0) return;
      setChartHeightPx((current) => (Math.abs(current - nextHeight) >= 1 ? nextHeight : current));
    };
    const schedule = (): void => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };
    schedule();
    const observer = new ResizeObserver(() => {
      schedule();
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    setChartHeightPx(statusChartVisualHeight);
  }, [statusChartVisualHeight]);

  const header = (
    <Box
      sx={(muiTheme) => ({
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        columnGap: s('lg'),
        rowGap: s('xs'),
        px: s('md'),
        minHeight: `${statusPanel.headerHeight}px`,
        boxSizing: 'border-box',
        [muiTheme.breakpoints.down('sm')]: {
          flexWrap: 'wrap',
        },
      })}
    >
      <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
        <PanelHeadingText
          component="h2"
          sx={{
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </PanelHeadingText>
      </Box>
      {legendNode ? (
        <Box
          sx={(muiTheme) => ({
            flex: '0 0 auto',
            minWidth: 0,
            maxWidth: '100%',
            marginLeft: 'auto',
            [muiTheme.breakpoints.down('sm')]: {
              flex: '1 0 100%',
              marginLeft: 0,
            },
          })}
        >
          {legendNode}
        </Box>
      ) : null}
    </Box>
  );

  const resolvedChartHeight = Math.max(140, chartHeightPx);

  return (
    <StatusChartPanel<TStatus>
      title={title}
      showToolbarControls={false}
      searchAriaLabel={searchAriaLabel}
      height={statusPanel.height}
      fixedHeightLayout
      fixedHeightPaddingBottom="md"
      panelPadding={statusPanel.panelPadding}
      panelContentGap={statusPanel.panelContentGap}
      headerHeight={statusPanel.headerHeight}
      headerTitlePaddingLeft={statusPanel.headerTitlePaddingLeft}
      headerControlsPaddingRight={statusPanel.headerControlsPaddingRight}
      emptyTitle={statusEmptyTitle}
      emptyBody={CHART_EMPTY_STATE_FILTER_HINT}
      onDebouncedSearchChange={toolbarState.setSearch}
      filterStatuses={toolbarState.selectedStatuses}
      statusOptions={statusOptions}
      onFilterStatusesChange={toolbarState.setSelectedStatuses}
      filterTags={toolbarState.selectedTags}
      onFilterTagsChange={toolbarState.setSelectedTags}
      dateQuickFilter={toolbarState.dateQuickFilter}
      onDateQuickFilterChange={toolbarState.onDateQuickFilterChange}
      fromDate={toolbarState.fromDate}
      toDate={toolbarState.toDate}
      latestQueryStartDay={toolbarState.latestQueryStartDay}
      latestQueryEndDay={toolbarState.latestQueryEndDay}
      onFromDateChange={(next) => {
        toolbarState.onFromDateChange(next ? next.format('YYYY-MM-DD') : null);
      }}
      onToDateChange={(next) => {
        toolbarState.onToDateChange(next ? next.format('YYYY-MM-DD') : null);
      }}
      availableTags={availableTags}
      statusFilterLabel={statusFilterLabel}
      headerOverride={header}
    >
      <Box ref={chartHostRef} sx={{ flex: '1 1 auto', minHeight: 0 }}>
        {showPlaceholder ? (
          <Box aria-hidden="true" sx={{ height: resolvedChartHeight }} />
        ) : loading || showStatusChartSkeleton ? (
          <StackedBarChartSkeleton
            height={resolvedChartHeight}
            plotAreaHeight={STATUS_TIMELINE_SKELETON_PLOT_AREA_HEIGHT_PX}
            hideYAxisLine
          />
        ) : showChartErrorState ? (
          <Box
            sx={{
              height: resolvedChartHeight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <StatusBanner
              tone="red"
              variant="outlined"
              message={getErrorCopy('banner.chartLoadFailed')}
              sx={chartLoadErrorBannerSx}
            />
          </Box>
        ) : statusChartData &&
          hasVisibleStackedChartValue(statusChartData.data, statusChartData.series) ? (
          <StackedBarChart
            data={statusChartData.data}
            series={statusChartData.series}
            height={resolvedChartHeight}
            showAllXAxisLabels={showAllXAxisLabels}
            xAxisLabelFormatter={formatStackedStatusChartLabel}
            tooltipLabelFormatter={formatChartTooltipDateLabel}
            marginLeft={statusPanel.chartMargins.left}
            marginRight={statusPanel.chartMargins.right}
            marginTop={statusPanel.headerToChartGap}
            marginBottom={statusPanel.chartMargins.bottom}
            yAxisWidth={statusPanel.yAxisWidth}
            yAxisAutoWidth={statusPanel.yAxisAutoWidth}
            yAxisAutoWidthPadding={statusPanel.yAxisAutoWidthPadding}
            yAxisHeadroomTicks={1}
            yAxisTopPaddingRatio={0}
            axisStrokeWidth={statusPanel.axisStrokeWidth}
            barCornerRadius={statusPanel.barCornerRadius}
            yAxisLabelOffset={statusPanel.yAxisLabelOffset}
            hideXAxisTicks={statusPanel.hideXAxisTicks}
            xAxisLabelOffset={statusPanel.xAxisLabelOffset}
            showHorizontalGrid
            showPlotAreaFrame
            showPlotAreaTopFrame={false}
            hideXAxisLine={false}
            hideYAxisLine
            skipAnimation={suppressAnimation}
            tooltipEmptyValueLabel="No runs"
          />
        ) : (
          <Box
            sx={{
              height: resolvedChartHeight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ChartEmptyState title={statusEmptyTitle} body={CHART_EMPTY_STATE_FILTER_HINT} />
          </Box>
        )}
      </Box>
    </StatusChartPanel>
  );
};
